import React, { useEffect, useState } from "react";
import { Loader2, ImageOff, ChevronLeft, ChevronRight, Film } from "lucide-react";
import { BACKEND_URL } from "../../lib/studioApi";

const Arrow = ({ dir, onClick, disabled, id }) => {
  const Icon = dir === "prev" ? ChevronLeft : ChevronRight;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={dir === "prev" ? "Previous slide" : "Next slide"}
      data-testid={`post-slide-${dir}-${id}`}
      className={`absolute top-1/2 -translate-y-1/2 ${dir === "prev" ? "left-2" : "right-2"} h-9 w-9 rounded-full grid place-items-center bg-black/70 backdrop-blur text-white border border-white/10 hover:border-[#BEFF38] transition-opacity disabled:opacity-0`}
    >
      <Icon className="w-5 h-5" />
    </button>
  );
};

const ReelPreview = ({ post, onReel }) => {
  const box = "relative w-full aspect-[9/16] max-w-[260px] mx-auto overflow-hidden rounded-xl bg-zinc-900 border border-zinc-800";
  if (post.reel_status === "ready" && post.reel_url) {
    return (
      <div className={box} data-testid={`post-reel-preview-${post.id}`}>
        <video key={post.reel_url} src={`${BACKEND_URL}${post.reel_url}`} controls playsInline loop className="absolute inset-0 w-full h-full object-cover" />
      </div>
    );
  }
  return (
    <div className={`${box} grid place-items-center p-6 text-center`} data-testid={`post-reel-empty-${post.id}`}>
      {post.reel_status === "rendering" ? (
        <span className="flex flex-col items-center gap-2 text-zinc-400 text-xs font-mono uppercase tracking-widest">
          <Loader2 className="w-6 h-6 animate-spin text-[#BEFF38]" /> Building reel…
        </span>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <Film className="w-8 h-8 text-[#BEFF38]" />
          <p className="text-zinc-400 text-xs">~25s kinetic reel: bold hook, demo shots, myth flips and level-up ladders, with a real track. Editing slides rebuilds it.</p>
          {post.reel_status === "failed" && <p className="text-red-400 text-[11px]">Last build failed — try again.</p>}
          <button
            onClick={onReel}
            disabled={post.image_status === "generating"}
            data-testid={`post-create-reel-${post.id}`}
            className="px-4 py-2 rounded-full bg-[#BEFF38] text-black text-[11px] font-black uppercase tracking-widest hover:bg-[#a8e62e] disabled:opacity-40"
          >
            Create reel
          </button>
        </div>
      )}
    </div>
  );
};

export const PostPreview = ({ post, format, onReel }) => {
  const [idx, setIdx] = useState(0);
  const slides = post.slide_urls || [post.feed_url];
  const generating = post.image_status === "generating";
  const story = format === "story" || format === "niggle";
  const multi = format !== "story";
  useEffect(() => setIdx(0), [post.version]);
  const cur = Math.min(idx, slides.length - 1);
  const url = `${BACKEND_URL}${multi ? slides[cur] : post.story_url}`;
  const vid = format === "story" ? post.story_video_url : format === "niggle" ? post.frame_video_urls?.[cur] : cur === 0 ? post.carousel_video_url : null;
  if (format === "reel") return <ReelPreview post={post} onReel={onReel} />;

  return (
    <div className="flex flex-col gap-2">
      <div
        data-testid={`post-preview-${post.id}`}
        className={`relative w-full overflow-hidden rounded-xl bg-zinc-900 border border-zinc-800 ${story ? "aspect-[9/16] max-w-[260px] mx-auto" : "aspect-[4/5]"}`}
      >
        {generating ? (
          <div className="absolute inset-0 grid place-items-center text-zinc-400 text-xs font-mono uppercase tracking-widest">
            <span className="flex flex-col items-center gap-2">
              <Loader2 className="w-6 h-6 animate-spin text-[#BEFF38]" /> Designing slides…
            </span>
          </div>
        ) : vid ? (
          <video key={vid} src={`${BACKEND_URL}${vid}`} poster={url} controls playsInline loop preload="metadata" data-testid={`post-video-${post.id}`} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <img key={url} src={url} alt={post.hook} className="absolute inset-0 w-full h-full object-cover animate-in fade-in duration-300" />
        )}
        {!generating && multi && slides.length > 1 && (
          <>
            <Arrow dir="prev" id={post.id} disabled={idx === 0} onClick={() => setIdx(idx - 1)} />
            <Arrow dir="next" id={post.id} disabled={idx >= slides.length - 1} onClick={() => setIdx(idx + 1)} />
          </>
        )}
        {post.image_status === "failed" && (
          <span className="absolute top-2 left-2 flex items-center gap-1 text-[10px] bg-black/80 text-amber-300 px-2 py-1 rounded-full">
            <ImageOff className="w-3 h-3" /> AI photo failed · text-only cover
          </span>
        )}
      </div>
      {multi && slides.length > 1 && (
        <div className="flex justify-center gap-1.5" data-testid={`post-slide-dots-${post.id}`}>
          {slides.map((_, i) => (
            <button
              key={i}
              aria-label={`Slide ${i + 1}`}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-[#BEFF38]" : "w-1.5 bg-zinc-700 hover:bg-zinc-500"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
